import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaArrowRight, FaPlay } from 'react-icons/fa';
import { DEMO_VIDEO_URL } from '../../constants';

/** Real Coursolo product UI — no mockups or generated dashboards. */
const FEATURED = {
  key: 'overview',
  src: '/Images/marketing/product-course-overview.png',
  altKey: 'landing.productProof.overviewAlt',
  altFallback: 'Coursolo course overview with modules, outcomes, and syllabus',
};

const PROOFS = [
  {
    key: 'dashboard',
    src: '/Images/marketing/product-courses-dashboard.png',
    altKey: 'landing.productProof.dashboardAlt',
    altFallback: 'Coursolo courses dashboard',
  },
  {
    key: 'practice',
    src: '/Images/marketing/product-lesson-practice.png',
    altKey: 'landing.productProof.practiceAlt',
    altFallback: 'Interactive lesson practice with reveal cards',
  },
  {
    key: 'assess',
    src: '/Images/marketing/product-blooms-quiz.png',
    altKey: 'landing.productProof.assessAlt',
    altFallback: "Bloom's Taxonomy practice quiz in Coursolo",
  },
  {
    key: 'lesson',
    src: '/Images/marketing/product-lesson-video.png',
    altKey: 'landing.productProof.lessonAlt',
    altFallback: 'Coursolo lesson with learn, practice, and reflect modes',
  },
];

export default function ProductProofStrip({ className = '', compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={`w-full ${className}`}>
      <div className="text-center max-w-2xl mx-auto mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-2">
          {t('landing.productProof.badge', 'Inside Coursolo')}
        </p>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
          {t('landing.productProof.title', 'What learners actually open')}
        </h3>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
          {t(
            'landing.productProof.subtitle',
            'A course library, structured modules, practice, and checks, built from the topics or sources you provide.',
          )}
        </p>
      </div>

      {/* Featured product frame */}
      {!compact && (
        <motion.figure
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-5 overflow-hidden rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 shadow-xl shadow-slate-900/10"
        >
          <div className="aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
            <img
              src={FEATURED.src}
              alt={t(FEATURED.altKey, FEATURED.altFallback)}
              className="h-full w-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <figcaption className="px-4 py-3 text-sm font-semibold text-slate-800 dark:text-slate-100 border-t border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95">
            {t('landing.productProof.captions.overview', 'Structured course overview & syllabus')}
          </figcaption>
        </motion.figure>
      )}

      <div className={`grid gap-4 md:gap-5 mb-8 ${compact ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
        {PROOFS.map((item, i) => (
          <motion.figure
            key={item.key}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="group relative overflow-hidden rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 shadow-lg shadow-slate-900/5"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={item.src}
                alt={t(item.altKey, item.altFallback)}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                loading="lazy"
              />
            </div>
            <figcaption className="px-3 py-2.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100 border-t border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90">
              {t(`landing.productProof.captions.${item.key}`)}
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/genui')}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm shadow-lg shadow-blue-500/25 hover:from-blue-600 hover:to-blue-700 transition-all"
        >
          {t('landing.productProof.ctaTry', 'Try course setup')}
          <FaArrowRight className="text-xs" aria-hidden />
        </button>
        <a
          href={DEMO_VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 font-semibold text-sm hover:border-blue-400 dark:hover:border-blue-500 transition-all"
        >
          <FaPlay className="text-xs text-blue-600" aria-hidden />
          {t('landing.productProof.ctaDemo', 'Watch demo')}
        </a>
      </div>
    </div>
  );
}
